// Rasterizes the .invoice-doc node into a PDF (rather than re-deriving the
// layout in a PDF library) so Download always matches what's on screen —
// same fonts, gradients, watermark, QR — with zero drift. html2canvas/
// jspdf are dynamically imported so their ~600KB doesn't sit in the main
// bundle for a rarely-used action.
//
// Print stays on the browser's native window.print() for the common
// case — a single-page invoice — on purpose: this was tried the other
// way (routing all of Print through this same rasterized pipeline)
// twice before, in an earlier round of fixes and then again in this
// one, and reverted back to native both times, because html2canvas's
// text always reads slightly "off" (different metrics/hinting) next to
// the browser's own crisp native rendering — a real, visible quality
// regression for the vast majority of invoices that fit on one page.
// Only a genuinely multi-page invoice routes through this PDF pipeline
// for Print too (see willFitOnOnePage / printInvoicePdf and
// invoices/view/page.tsx's decision between the two), because native
// print's position:fixed footer repeats on every physical page
// regardless of where that page's real content ends, which no CSS fix
// here could reconcile with a footer that's only supposed to appear
// where there's room for it.
//
// The closing block (marked .invoice-doc-footer in InvoiceDocument.tsx)
// and the header block (.invoice-doc-header) are captured separately here
// so each can be redrawn on whichever pages need them — see the slicing
// loop below.
//
// The watermark (marked .invoice-doc-watermark-wrapper) is captured and
// placed as its own third layer, explicitly centered on the full PDF
// page, rather than left to its own CSS position:absolute;inset:0 inside
// the content node. That centering is relative to whatever height the
// content node happens to have at capture time — which shrinks the
// instant the footer below it is hidden for the separate footer capture,
// visibly shifting the watermark upward. Centering it against the fixed
// page dimensions instead makes it immune to that.
const NAVY = '#1b2a4a';
const MM_PER_CSS_PX = 25.4 / 96;

// The logo/watermark/signature/stamp <img> tags point at signed Supabase
// Storage URLs (cross-origin from erp.thekseoul.com) with crossOrigin=
// "anonymous" set — which is enough for them to display normally on
// screen, but html2canvas still taints its canvas trying to read pixel
// data back out of them, and silently renders that element as a solid
// black box instead of throwing. Pre-fetching each cross-origin image and
// swapping its src to a same-origin data: URI before capture sidesteps
// canvas tainting entirely, regardless of the storage host's exact CORS
// headers. The watermark specifically also gets baked to grayscale here
// via a canvas — html2canvas doesn't reliably apply the live CSS
// filter:grayscale(1) on it, letting the original brand-red tone leak
// through faintly instead of the intended desaturated security-paper
// look. Both are swapped back to the original src/filter afterward so the
// live DOM keeps its real (signed, TTL'd) URL rather than an inlined
// snapshot.
async function inlineCrossOriginImages(node: HTMLElement): Promise<() => void> {
  const imgs = Array.from(node.querySelectorAll('img'));
  const restores: Array<() => void> = [];

  await Promise.all(
    imgs.map(async (img) => {
      const src = img.getAttribute('src');
      if (!src || src.startsWith('data:')) return;
      try {
        const res = await fetch(src, { mode: 'cors' });
        const blob = await res.blob();
        let dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });

        if (img.classList.contains('invoice-doc-watermark')) {
          dataUrl = await toGrayscaleDataUrl(dataUrl);
          const prevFilter = img.style.filter;
          img.style.filter = 'none';
          restores.push(() => {
            img.style.filter = prevFilter;
          });
        }

        restores.push(() => img.setAttribute('src', src));
        img.src = dataUrl;
        if (!img.complete) {
          await new Promise<void>((resolve) => {
            img.addEventListener('load', () => resolve(), { once: true });
            img.addEventListener('error', () => resolve(), { once: true });
          });
        }
      } catch {
        // Fetch itself failed (offline, etc.) — leave the original
        // crossOrigin src in place rather than blocking the whole capture.
      }
    }),
  );

  return () => restores.forEach((restore) => restore());
}

function toGrayscaleDataUrl(dataUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const px = imageData.data;
      for (let i = 0; i < px.length; i += 4) {
        // Standard luminance-weighted desaturation, matching what
        // CSS filter:grayscale(1) itself computes.
        const gray = 0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2];
        px[i] = gray;
        px[i + 1] = gray;
        px[i + 2] = gray;
      }
      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

// The INVOICE/TOTAL badges are solid navy boxes on screen, but used to
// print as plain navy text with no background — see the (now-unused for
// this path) .doc-badge print CSS in globals.css, which only applies to
// native window.print(). Since PDF generation no longer goes through
// window.print()/@media print at all, that rule never fires here — this
// reproduces the same look by temporarily overriding the same properties
// as inline styles just before capture, restored immediately after.
function applyPrintBadgeStyles(node: HTMLElement): () => void {
  const restores: Array<() => void> = [];

  node.querySelectorAll<HTMLElement>('.doc-badge').forEach((el) => {
    const prev = { background: el.style.background, padding: el.style.padding, borderRadius: el.style.borderRadius, minWidth: el.style.minWidth };
    el.style.background = 'none';
    el.style.padding = '0';
    el.style.borderRadius = '0';
    el.style.minWidth = '0';
    restores.push(() => Object.assign(el.style, prev));
  });

  node.querySelectorAll<HTMLElement>('.doc-badge-value').forEach((el) => {
    const prevColor = el.style.color;
    el.style.color = NAVY;
    restores.push(() => {
      el.style.color = prevColor;
    });
  });

  return () => restores.forEach((restore) => restore());
}

type Html2Canvas = typeof import('html2canvas').default;

// Captures the watermark (image or the plain-"K" text fallback, either
// way wrapped in .invoice-doc-watermark-wrapper) on its own, in isolation,
// with a transparent backing — then hides the live one so it doesn't also
// get baked into the main content capture at its (height-dependent, so
// unreliable) CSS position. Returns null if there's no watermark wrapper
// at all.
async function captureWatermarkLayer(
  node: HTMLElement,
  html2canvas: Html2Canvas,
): Promise<{ dataUrl: string; widthMm: number; heightMm: number; restore: () => void } | null> {
  const wrapper = node.querySelector<HTMLElement>('.invoice-doc-watermark-wrapper');
  if (!wrapper) return null;

  const rect = wrapper.getBoundingClientRect();
  // Matches the scale used for the rest of the document (see
  // captureOptions in buildInvoicePdf) so the watermark isn't noticeably
  // blurrier than everything drawn on top of it.
  const canvas = await html2canvas(wrapper, { scale: 3, useCORS: true, backgroundColor: null });
  const prevDisplay = wrapper.style.display;
  wrapper.style.display = 'none';

  return {
    dataUrl: canvas.toDataURL('image/png'),
    widthMm: rect.width * MM_PER_CSS_PX,
    heightMm: rect.height * MM_PER_CSS_PX,
    restore: () => {
      wrapper.style.display = prevDisplay;
    },
  };
}

async function buildInvoicePdf(container: HTMLElement) {
  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);
  const node = container.querySelector<HTMLElement>('.invoice-doc') ?? container;
  const footerNode = node.querySelector<HTMLElement>('.invoice-doc-footer');
  const headerNode = node.querySelector<HTMLElement>('.invoice-doc-header');
  // letterRendering:true was tried here to improve text fidelity, but
  // html2canvas renders each letter as its own separate fillText call in
  // that mode, which occasionally drops a thin stroke off certain glyphs
  // at this font size (a "+" losing its vertical stroke and reading as a
  // "-" was one instance) — worse than the default batched text
  // rendering it was meant to improve on.
  // scale:2 (~192 DPI at A4 width) read soft/blurry on an actual printout
  // or zoomed-in PDF — text edges and the header/totals boxes' rounded
  // corners all looked a little "melted" compared to the crisp live page,
  // which is what read as a missing "3D"/depth effect on those boxes:
  // there never was a box-shadow on them (there's no boxShadow anywhere
  // in this component), it was the blur itself softening their edges.
  // scale:3 (~288 DPI, close to the 300 DPI print convention) fixes that
  // directly. Cheap to afford since this only ever runs on a manual
  // Print/Download click, not on every render.
  const captureOptions = { scale: 3, useCORS: true, backgroundColor: '#ffffff' } as const;

  const restoreImages = await inlineCrossOriginImages(node);
  const restoreBadges = applyPrintBadgeStyles(node);
  // Fraunces/Playfair Display and Inter load from Google Fonts with
  // display:swap (index.html) — the page first paints with a fallback
  // (Georgia/system-ui) and swaps to the real face once it's downloaded.
  // On screen that swap has almost always already happened by the time a
  // person actually clicks Print/Download, so it goes unnoticed; without
  // waiting for it here, a capture taken too soon after page load bakes
  // the fallback font into the PDF, which is what read as "the on-screen
  // page looks premium, the PDF/print doesn't" — same layout, just a
  // plainer typeface. document.fonts.ready is the browser's own signal
  // that every requested font has finished loading and the swap (if any)
  // has occurred, so waiting for it makes the capture's font state match
  // the live page's, not just whatever happened to be loaded yet.
  await document.fonts.ready;
  // A settle beat after all the DOM/style mutations above and the font
  // swap — html2canvas snapshots computed styles synchronously, and
  // capturing mid-reflow is what caused the "font alignment off" look.
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

  // Explicit type annotation (rather than relying on inference from the
  // assignment below) — the new drawWatermarkOnCurrentPage() closure
  // further down captures `pdf` before TS's control-flow narrowing can see
  // it's been assigned, which otherwise falls back to implicit `any`.
  let pdf: InstanceType<typeof jsPDF>;
  try {
    pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const watermark = await captureWatermarkLayer(node, html2canvas);
    try {
      // Re-drawn on every page a long invoice spills onto (not just the
      // first), so a multi-page document still reads as one continuous
      // "security paper" rather than losing the watermark after page 1.
      const drawWatermarkOnCurrentPage = () => {
        if (!watermark) return;
        pdf.addImage(
          watermark.dataUrl,
          'PNG',
          (pageWidth - watermark.widthMm) / 2,
          (pageHeight - watermark.heightMm) / 2,
          watermark.widthMm,
          watermark.heightMm,
        );
      };

      // The content node's own bg-white would otherwise paint over the
      // watermark layer just placed above — dropped for this one capture
      // (transparent canvas, alpha preserved) so the watermark shows
      // through wherever the content itself doesn't paint something
      // opaque, same as it does live on screen.
      const prevBg = node.style.backgroundColor;
      node.style.backgroundColor = 'transparent';

      if (footerNode) {
        const footerCanvas = await html2canvas(footerNode, captureOptions);
        const footerHeightMm = (footerCanvas.height * pageWidth) / footerCanvas.width;

        // Hide the footer just for this one capture so it doesn't appear
        // twice (once here, once in its own capture above) — restored
        // immediately.
        const prevDisplay = footerNode.style.display;
        footerNode.style.display = 'none';
        const contentCanvas = await html2canvas(node, { ...captureOptions, backgroundColor: null });
        footerNode.style.display = prevDisplay;
        node.style.backgroundColor = prevBg;
        const contentHeightMm = (contentCanvas.height * pageWidth) / contentCanvas.width;

        drawWatermarkOnCurrentPage();

        // The overwhelmingly common case — content AND the footer both
        // fit on one page — is handled by this exact, untouched branch:
        // draw the whole capture once, pin the footer under it (or to the
        // page's own bottom edge if content is short) via the same
        // Math.max this pipeline always used. Deliberately kept
        // independent of the multi-page branch below so a normal
        // invoice's output can never be affected by changes made there.
        //
        // Checking contentHeightMm alone (as this used to) is wrong: a
        // content-only height that's just under pageHeight still leaves
        // no room for the footer stacked below it, so footerY ends up
        // past the page's bottom edge and jsPDF silently clips the whole
        // footer off — invisible in the saved PDF with no error. Content
        // + footer together must fit, or this needs to fall through to
        // the slicing branch below (which already reserves footer space
        // correctly and still produces a single page whenever the two
        // actually do fit together).
        if (contentHeightMm + footerHeightMm <= pageHeight) {
          pdf.addImage(contentCanvas.toDataURL('image/png'), 'PNG', 0, 0, pageWidth, contentHeightMm);
          const footerY = Math.max(contentHeightMm, pageHeight - footerHeightMm);
          pdf.addImage(footerCanvas.toDataURL('image/png'), 'PNG', 0, footerY, pageWidth, footerHeightMm);
          return pdf;
        }

        // Every line-item row's top edge, plus the top edge of any block
        // marked .invoice-doc-keep-together (Totals; the Payment Method/
        // Terms/Notes row) — in the same pixel space as contentCanvas.
        // A page break is only ever placed at one of these (or at the
        // true end of content), so a single item's serial list, or one
        // of those boxes, never gets sliced through the middle onto two
        // pages. Measured off the live DOM, not the canvas, since that's
        // the only place these boundaries are known; unaffected by the
        // footer being hidden above (an earlier grid row's rows never
        // move when a later row's content changes).
        const nodeRect = node.getBoundingClientRect();
        const canvasPxPerCssPx = contentCanvas.width / nodeRect.width;
        const rowBreakPointsPx = Array.from(node.querySelectorAll('tbody tr, .invoice-doc-keep-together')).map(
          (el) => (el.getBoundingClientRect().top - nodeRect.top) * canvasPxPerCssPx,
        );

        // Captured once and re-drawn at the top of every page after the
        // first — without this, a continuation page would open mid-table
        // with no way to tell which invoice it's even for if it were
        // ever separated from page 1.
        const headerCanvas = headerNode ? await html2canvas(headerNode, captureOptions) : null;
        const headerHeightMm = headerCanvas ? (headerCanvas.height * pageWidth) / headerCanvas.width : 0;

        // Every page gets the footer at its bottom now, not just the
        // last one — same reasoning as the repeated header: a page
        // separated from the rest of the invoice should still carry the
        // QR/thank-you/legal block, not just look cut off. A long line
        // item list (many scanned serials) used to just get crammed past
        // the visible page edge instead of flowing onto a page 2, with
        // the footer landing at that same off-page Y. Slicing the
        // content into page-height bands (each page's own budget reduced
        // by whichever of the repeated header/footer apply to it) and
        // giving each slice its own PDF page fixes both at once: nothing
        // is ever drawn past a page's real edge, overflow genuinely
        // continues on the next page — landing, where possible, exactly
        // between two items (or before Totals/Payment Method) rather
        // than mid-row — and the footer always has reserved room to sit
        // in.
        const pxPerMm = contentCanvas.width / pageWidth;
        const pageHeightPx = pageHeight * pxPerMm;
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = contentCanvas.width;
        const sliceCtx = sliceCanvas.getContext('2d');

        const headerHeightPx = headerCanvas ? (headerCanvas.height * contentCanvas.width) / headerCanvas.width : 0;
        const footerHeightPx = footerHeightMm * pxPerMm;

        let cursorPx = 0;
        let isFirstPage = true;
        while (cursorPx < contentCanvas.height) {
          const reservedPx = (isFirstPage ? 0 : headerHeightPx) + footerHeightPx;
          const availableHeightPx = pageHeightPx - reservedPx;
          const remainingPx = contentCanvas.height - cursorPx;
          let sliceHeightPx: number;
          if (remainingPx <= availableHeightPx) {
            // Everything left fits on this (the last) page — no need to
            // look for an earlier row boundary to break at.
            sliceHeightPx = remainingPx;
          } else {
            const hardCapPx = cursorPx + availableHeightPx;
            const rowBreaksThatFit = rowBreakPointsPx.filter((y) => y > cursorPx + 1 && y <= hardCapPx);
            // Falls back to a hard cut at the page limit only when no row
            // boundary fits at all within one page (an exceptional single
            // item whose own serial list alone is taller than a full
            // page) — otherwise progress would stall.
            sliceHeightPx = rowBreaksThatFit.length > 0 ? Math.max(...rowBreaksThatFit) - cursorPx : availableHeightPx;
          }
          sliceCanvas.height = sliceHeightPx;
          sliceCtx?.clearRect(0, 0, sliceCanvas.width, sliceHeightPx);
          sliceCtx?.drawImage(contentCanvas, 0, cursorPx, contentCanvas.width, sliceHeightPx, 0, 0, contentCanvas.width, sliceHeightPx);

          if (!isFirstPage) {
            pdf.addPage();
            drawWatermarkOnCurrentPage();
          }
          const sliceHeightMm = sliceHeightPx / pxPerMm;
          const sliceYMm = isFirstPage || !headerCanvas ? 0 : headerHeightMm;
          if (!isFirstPage && headerCanvas) {
            pdf.addImage(headerCanvas.toDataURL('image/png'), 'PNG', 0, 0, pageWidth, headerHeightMm);
          }
          pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', 0, sliceYMm, pageWidth, sliceHeightMm);

          // Bottom-aligned on this page whenever there's room to spare (a
          // short last page) — otherwise flush against the content just
          // drawn, which on a fully-packed page lands it exactly at the
          // page's own bottom edge anyway.
          const contentBottomMm = sliceYMm + sliceHeightMm;
          const footerY = Math.max(contentBottomMm, pageHeight - footerHeightMm);
          pdf.addImage(footerCanvas.toDataURL('image/png'), 'PNG', 0, footerY, pageWidth, footerHeightMm);

          cursorPx += sliceHeightPx;
          isFirstPage = false;
        }
      } else {
        drawWatermarkOnCurrentPage();
        const canvas = await html2canvas(node, { ...captureOptions, backgroundColor: null });
        node.style.backgroundColor = prevBg;
        const imgHeight = (canvas.height * pageWidth) / canvas.width;
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pageWidth, imgHeight);
      }
    } finally {
      watermark?.restore();
    }
  } finally {
    restoreBadges();
    restoreImages();
  }

  return pdf;
}

// Cheap, capture-free check for whether the live .invoice-doc would need
// more than one A4 page — just the element's own rendered height (which
// already includes the footer, in normal document flow beneath the
// content) converted from CSS px to mm. Used by invoices/view/page.tsx
// (and the other Print entry points) to decide Print's path *before*
// doing any work: native window.print() for the single-page case (see
// this file's header comment for why that's deliberate), the slower
// rasterized pipeline only when a page break is actually going to be
// needed. A4's height is 297mm; a couple of mm of slack errs toward
// native print on a borderline invoice, since native is the crisper
// choice for anything that's actually going to fit.
export function willFitOnOnePage(container: HTMLElement): boolean {
  const node = container.querySelector<HTMLElement>('.invoice-doc') ?? container;
  const heightMm = node.getBoundingClientRect().height * MM_PER_CSS_PX;
  return heightMm <= 297;
}

export async function downloadInvoicePdf(container: HTMLElement, filename: string): Promise<void> {
  const pdf = await buildInvoicePdf(container);
  pdf.save(filename);
}

// Print's fallback for the invoice genuinely spanning multiple A4 pages
// (see willFitOnOnePage — invoices/view/page.tsx only reaches for this
// when that returns false; a single-page invoice prints natively instead,
// per this file's header comment). Native print relies on a position:
// fixed footer to sit at the true bottom of each physical page — which,
// being fixed, repeats on every page regardless of where that page's own
// flowing content actually ends, so a genuinely multi-page invoice still
// overlapped its footer with real content even with break-inside allowed
// to be 'auto'. This file's page-by-page slicing already solves that
// correctly for Download, so reusing it here means the printed pages
// actually match the PDF instead of fighting print's own pagination
// rules — worth the html2canvas rasterized-text tradeoff only for this
// rarer case, where correct pagination matters more than pixel-perfect
// native text.
//
// Loads the PDF into a hidden <iframe> and calls print() on THAT window,
// rather than jsPDF's own autoPrint() (which embeds a PDF-level "run
// print() on open" action). autoPrint() depends on the browser's PDF
// viewer actually executing embedded PDF JavaScript — Chrome's built-in
// viewer (PDFium) frequently doesn't, for security reasons, so the PDF
// would just open with nothing happening. iframe.contentWindow.print()
// is the browser's own print(), not something the PDF has to cooperate
// with, so it's reliable regardless of what the PDF viewer does or does
// not support. It also sidesteps window.open()'s user-activation timing
// entirely, since nothing here opens a new tab/window at all.
export async function printInvoicePdf(container: HTMLElement): Promise<void> {
  const pdf = await buildInvoicePdf(container);
  const blobUrl = pdf.output('bloburl').toString();

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  iframe.src = blobUrl;
  document.body.appendChild(iframe);

  await new Promise<void>((resolve) => {
    iframe.onload = () => resolve();
  });
  // A settle beat for the PDF viewer inside the iframe to actually paint
  // before print() is called on it — calling immediately on 'load' has
  // been unreliable for embedded PDF viewers specifically (unlike a
  // normal HTML page, 'load' can fire before the viewer's own render is
  // ready to be printed).
  await new Promise((resolve) => setTimeout(resolve, 300));

  iframe.contentWindow?.focus();
  iframe.contentWindow?.print();

  // Left in the DOM well past when the print dialog would have appeared
  // (and been accepted/cancelled) before cleanup — removing the iframe
  // too early cancels an in-progress print in some browsers.
  setTimeout(() => {
    iframe.remove();
  }, 60_000);
}
